import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { usePrefix, useT } from "@/i18n";

const IMAGES = [
  "g01", "g02", "g03", "g04", "g05", "g06", "g07", "g08", "g09", "g10",
  "g11", "g12", "g13", "g14", "g15", "g16", "g17", "g18", "g19", "g20",
  "g21", "g22", "g23", "g24", "g25", "g26", "g28", "g30", "g31", "g32",
  "g33", "g34", "g35", "g36",
].map((n) => `/images/gallery/${n}.webp`);

export default function GalleryPage() {
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const t = useT();
  const g = t.gallery;
  const prefix = usePrefix();

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (d) => setOpen((i) => (i === null ? null : (i + d + IMAGES.length) % IMAGES.length)),
    []
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, step]);

  const goContact = () => {
    navigate(prefix || "/", { state: { scrollTo: "#contact" } });
  };

  return (
    <main className="mx-auto max-w-[1440px] px-6 pb-28 pt-36 lg:px-12 lg:pb-40 lg:pt-44" data-testid="gallery-page">
      <Seo title={t.seo.galleryTitle} description={t.seo.galleryDesc} locale={t.seo.locale} />
      <Reveal>
        <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-terra" data-testid="gallery-kicker">{g.kicker}</p>
        <h1 className="max-w-3xl font-serif text-4xl tracking-tight sm:text-5xl lg:text-6xl" data-testid="gallery-heading">
          {g.headingA} <em className="italic text-terra">{g.headingEm}</em>
        </h1>
        <p className="mt-6 max-w-xl text-base text-smoke md:text-lg" data-testid="gallery-subtext">
          {g.subtext}
        </p>
      </Reveal>

      <div className="mt-16 columns-1 gap-6 sm:columns-2 xl:columns-3" data-testid="gallery-grid">
        {IMAGES.map((src, i) => (
          <motion.button
            key={src}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group mb-6 block w-full break-inside-avoid text-left"
            onClick={() => setOpen(i)}
            data-testid={`gallery-image-${i + 1}`}
          >
            <div className="frame">
              <div className="overflow-hidden">
                <img
                  src={src}
                  alt={`${g.imgAlt} ${i + 1}`}
                  loading="lazy"
                  className="w-full cursor-zoom-in object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      <Reveal className="mt-24 border-t border-ink/15 pt-16 text-left">
        <h2 className="font-serif text-3xl tracking-tight sm:text-4xl" data-testid="gallery-cta-heading">
          {g.ctaHeading}
        </h2>
        <p className="mt-4 max-w-xl text-base text-smoke">
          {g.ctaText}
        </p>
        <a
          href={`${prefix ? `${prefix}/` : "/"}#contact`}
          data-testid="gallery-cta-contact"
          onClick={(e) => { e.preventDefault(); goContact(); }}
          className="group mt-8 inline-flex items-center gap-3 bg-terra px-8 py-4 text-xs uppercase tracking-[0.22em] text-bone transition-colors duration-300 hover:bg-ink"
        >
          {g.ctaButton}
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </a>
      </Reveal>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 backdrop-blur-md"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="absolute right-6 top-6 text-bone/70 transition-colors duration-300 hover:text-terra"
              aria-label={g.close}
            >
              <X size={28} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-bone/70 transition-colors duration-300 hover:text-terra lg:left-10"
              aria-label={g.prev}
            >
              <ChevronLeft size={40} />
            </button>
            <motion.img
              key={IMAGES[open]}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={IMAGES[open]}
              alt={`${g.imgAlt} ${open + 1}`}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-bone/70 transition-colors duration-300 hover:text-terra lg:right-10"
              aria-label={g.next}
            >
              <ChevronRight size={40} />
            </button>
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 font-serif text-lg italic text-bone/60" data-testid="lightbox-counter">
              {open + 1} / {IMAGES.length}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
